import { useQuery } from '@tanstack/react-query';
import { AnalyticsService } from '../api';

export const useAnalyticsStats = () => {
  return useQuery({
    queryKey: ['analytics', 'stats'],
    queryFn: AnalyticsService.getStats,
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    staleTime: 15 * 1000, // 15 seconds
  });
};

export const usePlayAnalytics = (params: {
  period?: 'day' | 'week' | 'month' | 'year';
  limit?: number;
} = {}) => {
  return useQuery({
    queryKey: ['analytics', 'plays', params],
    queryFn: () => AnalyticsService.getPlayAnalytics(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUploadAnalytics = (params: {
  period?: 'day' | 'week' | 'month' | 'year';
  start_date?: string;
  end_date?: string;
} = {}) => {
  return useQuery({
    queryKey: ['analytics', 'uploads', params],
    queryFn: () => AnalyticsService.getUploadAnalytics(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSystemHealth = () => {
  return useQuery({
    queryKey: ['analytics', 'system', 'health'],
    queryFn: AnalyticsService.getSystemHealth,
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    staleTime: 15 * 1000, // 15 seconds
  });
};

export const useRecentActivity = (params: {
  limit?: number;
  type?: string;
  user_id?: string;
} = {}) => {
  return useQuery({
    queryKey: ['analytics', 'activity', params],
    queryFn: () => AnalyticsService.getRecentActivity(params),
    refetchInterval: 60 * 1000, // Refetch every minute
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useUsagePatterns = (params: {
  period?: 'day' | 'week' | 'month';
  metric?: 'plays' | 'uploads' | 'users';
} = {}) => {
  return useQuery({
    queryKey: ['analytics', 'usage-patterns', params],
    queryFn: () => AnalyticsService.getUsagePatterns(params),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useContentPerformance = (params: {
  period?: 'day' | 'week' | 'month' | 'year';
  sort_by?: 'plays' | 'rating' | 'engagement';
  limit?: number;
} = {}) => {
  return useQuery({
    queryKey: ['analytics', 'content-performance', params],
    queryFn: () => AnalyticsService.getContentPerformance(params),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUserEngagement = (params: {
  period?: 'day' | 'week' | 'month';
  user_id?: string;
} = {}) => {
  return useQuery({
    queryKey: ['analytics', 'user-engagement', params],
    queryFn: () => AnalyticsService.getUserEngagement(params),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useStorageAnalytics = () => {
  return useQuery({
    queryKey: ['analytics', 'storage'],
    queryFn: AnalyticsService.getStorageAnalytics,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useQualityMetrics = (params: {
  period?: 'day' | 'week' | 'month';
  content_type?: string;
} = {}) => {
  return useQuery({
    queryKey: ['analytics', 'quality', params],
    queryFn: () => AnalyticsService.getQualityMetrics(params),
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
};